/* ==========================================================================
   Dynamic Article Store (Unified Seed & API Data)
   Provides reactive, multilingual access to News, Case Studies & Product Updates
   with automatic fallback to bundled static data when offline.
   ========================================================================== */

import scrapedData from '../../scraped_case_studies_full.json';
import { articleIndexTranslations } from '../locales/articleTranslationsData.js';
import { articleFullTranslations } from '../locales/articleFullTranslations.js';
import { getLocale } from '../locales/i18n.js';

// Internal Store Map
const articlesMap = new Map();
const subscribers = new Set();
let isInitialized = false;

/**
 * Initialize Store with bundled static data
 */
function seedStaticArticles() {
  const languages = ['cs', 'en', 'de', 'es', 'pt', 'ar', 'it'];

  Object.entries(scrapedData || {}).forEach(([slug, raw]) => {
    if (!raw || !slug) return;

    const normalizedSlug = slug.replace('#', '').replace('/case-studies/', '').replace('/latest-news/', '').replace('/product-updates/', '').replace('/detection/', '').replace(/\/$/, '') || slug;
    
    // Build translations object from articleTranslationsData & articleFullTranslations
    const translations = {};
    for (const lang of languages) {
      const idx = (articleIndexTranslations[lang] && articleIndexTranslations[lang][normalizedSlug]) || {};
      const full = (articleFullTranslations[lang] && articleFullTranslations[lang][normalizedSlug]) || {};
      
      translations[lang] = {
        title: full.title || idx.title || raw.title || '',
        excerpt: full.excerpt || idx.excerpt || raw.excerpt || '',
        category: full.category || idx.category || raw.category || '',
        content: full.content || raw.content || '',
        date: full.date || idx.date || raw.date || ''
      };
    }

    let articleImg = raw.img || '';
    if (normalizedSlug === 'sed-kiss-integration-automated-alerts') {
      const currentLang = getLocale() || 'en';
      articleImg = currentLang === 'cs' ? '/images/propojení SED a KISS_CZ.png' : '/images/SED_a_KISS.png';
    }

    const articleObj = {
      slug: normalizedSlug,
      category: (raw.category || 'latest-news').toLowerCase(),
      date: raw.date || '',
      tag: raw.tag || '',
      publisher: raw.publisher || 'JALUD Embedded',
      img: articleImg,
      yt: raw.yt || '',
      videos: raw.videos || (raw.yt ? [raw.yt] : []),
      translations,
      crossReferences: raw.crossReferences || [],
      relatedArticles: raw.relatedArticles || []
    };

    articlesMap.set(normalizedSlug, articleObj);
  });
}

/**
 * Asynchronously fetch latest articles from REST API and merge into store
 */
export async function syncArticlesFromApi() {
  try {
    const res = await fetch('/api/articles');
    if (!res.ok) throw new Error(`API returned status ${res.status}`);
    const data = await res.json();
    
    if (data && Array.isArray(data.articles)) {
      data.articles.forEach(art => {
        if (art && art.slug) {
          articlesMap.set(art.slug, {
            ...articlesMap.get(art.slug),
            ...art
          });
        }
      });
      notifySubscribers();
    }
  } catch (err) {
    // Graceful offline fallback - store already contains seeded static articles
    console.debug('[ArticleStore] API sync skipped or offline, using static store:', err.message);
  }
}

/**
 * Initialize store once
 */
export function initArticleStore() {
  if (isInitialized) return;
  isInitialized = true;
  seedStaticArticles();
  syncArticlesFromApi();
}

/**
 * Subscribe to store mutations
 */
export function subscribeArticleStore(fn) {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

function notifySubscribers() {
  subscribers.forEach(fn => {
    try { fn(); } catch (e) { console.error('[ArticleStore] subscriber error:', e); }
  });
}

/**
 * Robust date parser supporting English, Czech/Slovak, German, ISO and European dot formats.
 * Returns Unix timestamp in milliseconds.
 */
export function parseArticleDate(dStr) {
  if (!dStr) return 0;
  if (typeof dStr === 'number') return isNaN(dStr) ? 0 : dStr;
  const str = String(dStr).trim();
  if (!str) return 0;

  // 1. European dot format: D. M. YYYY or DD.MM.YYYY (e.g., 23. 9. 2026, 1. 9. 2024, 15.08.2024)
  const dotMatch = str.match(/^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})/);
  if (dotMatch) {
    const day = parseInt(dotMatch[1], 10);
    const month = parseInt(dotMatch[2], 10) - 1;
    const year = parseInt(dotMatch[3], 10);
    const d = new Date(year, month, day);
    if (!isNaN(d.getTime())) return d.getTime();
  }

  // 2. Czech / Slovak month names: D. [měsíc] YYYY
  const czMonths = {
    'leden': 0, 'ledna': 0, 'únor': 1, 'února': 1, 'unor': 1, 'unora': 1,
    'březen': 2, 'března': 2, 'brezen': 2, 'brezna': 2,
    'duben': 3, 'dubna': 3, 'květen': 4, 'května': 4, 'kveten': 4, 'kvetna': 4,
    'červen': 5, 'června': 5, 'cerven': 5, 'cervna': 5,
    'červenec': 6, 'července': 6, 'cervenec': 6, 'cervence': 6,
    'srpen': 7, 'srpna': 7, 'září': 8, 'zari': 8,
    'říjen': 9, 'října': 9, 'rijen': 9, 'rijna': 9,
    'listopad': 10, 'listopadu': 10, 'prosinec': 11, 'prosince': 11
  };
  const czMatch = str.match(/^(\d{1,2})\.\s*([a-záčďéěíňóřšťúůýž]+)\s+(\d{4})/i);
  if (czMatch) {
    const day = parseInt(czMatch[1], 10);
    const mName = czMatch[2].toLowerCase();
    const year = parseInt(czMatch[3], 10);
    if (czMonths[mName] !== undefined) {
      const d = new Date(year, czMonths[mName], day);
      if (!isNaN(d.getTime())) return d.getTime();
    }
  }

  // 3. German month names: D. [Monat] YYYY
  const deMonths = {
    'januar': 0, 'februar': 1, 'märz': 2, 'maerz': 2, 'april': 3, 'mai': 4,
    'juni': 5, 'juli': 6, 'august': 7, 'september': 8, 'oktober': 9,
    'november': 10, 'dezember': 11
  };
  const deMatch = str.match(/^(\d{1,2})\.\s*([a-zäöüß]+)\s+(\d{4})/i);
  if (deMatch) {
    const day = parseInt(deMatch[1], 10);
    const mName = deMatch[2].toLowerCase();
    const year = parseInt(deMatch[3], 10);
    if (deMonths[mName] !== undefined) {
      const d = new Date(year, deMonths[mName], day);
      if (!isNaN(d.getTime())) return d.getTime();
    }
  }

  // 4. ISO or standard English date (e.g., September 23, 2026, 2026-09-23)
  const d = new Date(str);
  if (!isNaN(d.getTime())) return d.getTime();

  // 5. Fallback for non-standard initial release tokens
  if (/official|release/i.test(str)) {
    return new Date(2023, 0, 1).getTime();
  }

  return 0;
}

/**
 * Format date for localized display
 */
export function formatArticleDate(dStr, lang) {
  if (!dStr) return '';
  const ts = parseArticleDate(dStr);
  if (!ts) return dStr;
  const d = new Date(ts);
  if (lang === 'cs') {
    return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
  }
  return dStr;
}

/**
 * Get all articles sorted strictly by date descending (newest first)
 */
export function getAllArticles() {
  if (!isInitialized) initArticleStore();
  const list = Array.from(articlesMap.values());
  list.sort((a, b) => {
    const timeA = parseArticleDate(a.date || (a.translations && a.translations.en && a.translations.en.date) || a.updatedAt);
    const timeB = parseArticleDate(b.date || (b.translations && b.translations.en && b.translations.en.date) || b.updatedAt);
    return timeB - timeA;
  });
  return list;
}

/**
 * Get articles filtered by category, sorted newest first
 */
export function getArticlesByCategory(category) {
  const all = getAllArticles();
  if (!category) return all;
  const catLower = category.toLowerCase();
  return all.filter(a => {
    const c = (a.category || '').toLowerCase();
    if (catLower === 'latest-news' || catLower === 'news') {
      return c === 'latest-news' || c === 'news';
    }
    if (catLower === 'product-updates' || catLower === 'product-update') {
      return c === 'product-updates' || c === 'product-update';
    }
    if (catLower === 'case-studies' || catLower === 'case-study') {
      return c === 'case-studies' || c === 'case-study';
    }
    return c === catLower;
  });
}

/**
 * Get single article by slug with localized fields
 */
export function getArticleBySlug(slugOrId, targetLang) {
  if (!isInitialized) initArticleStore();
  const lang = targetLang || getLocale() || 'cs';
  const cleanId = (slugOrId || '').replace('#', '').replace('/case-studies/', '').replace('/latest-news/', '').replace('/product-updates/', '').replace('/detection/', '').replace(/\/$/, '') || slugOrId;

  const raw = articlesMap.get(cleanId) || articlesMap.get(slugOrId);
  if (!raw) return null;

  const trans = (raw.translations && raw.translations[lang]) || (raw.translations && raw.translations.cs) || (raw.translations && raw.translations.en) || {};

  let displayImg = raw.img;
  if (cleanId === 'sed-kiss-integration-automated-alerts') {
    displayImg = lang === 'cs' ? '/images/propojení SED a KISS_CZ.png' : '/images/SED_a_KISS.png';
  }

  return {
    ...raw,
    img: displayImg,
    title: trans.title || raw.title || '',
    excerpt: trans.excerpt || raw.excerpt || '',
    categoryDisplay: trans.category || (raw.category || '').replace('-', ' ').toUpperCase(),
    content: trans.content || raw.content || '',
    date: trans.date || raw.date || '',
    activeLang: lang
  };
}

// Auto-initialize on module load
initArticleStore();
